// src/components/preferences/LocationPreferenceEditor.tsx
/**
 * Editor for location preferences (cities, countries, remote preferences)
 * ⚠️ FIXED: Uses correct field names matching backend schema
 */
import React from 'react';
import {
  IonItem,
  IonLabel,
  IonInput,
  IonToggle,
  IonList,
  IonChip,
  IonIcon,
  IonText,
  IonButton,
} from '@ionic/react';
import { closeCircle } from 'ionicons/icons';
import { LocationPreferences } from '../../types/preferences.ts';
import './PreferenceEditor.css';

interface LocationPreferenceEditorProps {
  data: LocationPreferences;
  onChange: (data: LocationPreferences) => void;
}

const LocationPreferenceEditor: React.FC<LocationPreferenceEditorProps> = ({ data, onChange }) => {
  const [newCity, setNewCity] = React.useState('');
  const [newCountry, setNewCountry] = React.useState('');

  const handleToggle = (field: keyof LocationPreferences, value: boolean) => {
    onChange({ ...data, [field]: value });
  };

  const handleAddItem = (
    field: 'cities' | 'countries',
    value: string,
    setValue: (v: string) => void
  ) => {
    if (value.trim()) {
      const currentList = (data[field] || []) as string[];
      onChange({
        ...data,
        [field]: [...currentList, value.trim()],
      });
      setValue('');
    }
  };

  const handleRemoveItem = (
    field: 'cities' | 'countries',
    index: number
  ) => {
    const currentList = (data[field] || []) as string[];
    onChange({
      ...data,
      [field]: currentList.filter((_, i) => i !== index),
    });
  };

  const renderChipInput = (
    field: 'cities' | 'countries',
    label: string,
    placeholder: string,
    value: string,
    setValue: (v: string) => void
  ) => {
    const items = (data[field] || []) as string[];

    return (
      <IonItem>
        <IonLabel position="stacked">{label}</IonLabel>
        <div className="chip-input-container">
          {items.length > 0 && (
            <div className="chip-list">
              {items.map((item, index) => (
                <IonChip key={index} color="secondary">
                  {item}
                  <IonIcon
                    icon={closeCircle}
                    onClick={() => handleRemoveItem(field, index)}
                  />
                </IonChip>
              ))}
            </div>
          )}
          <div className="chip-input">
            <IonInput
              value={value}
              placeholder={placeholder}
              onIonChange={(e) => setValue(e.detail.value || '')}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddItem(field, value, setValue);
                }
              }}
            />
            <IonButton onClick={() => handleAddItem(field, value, setValue)} size="small">
              Add
            </IonButton>
          </div>
        </div>
      </IonItem>
    );
  };

  return (
    <IonList className="preference-editor-list">
      {renderChipInput('cities', 'Preferred Cities', 'e.g., San Francisco, CA', newCity, setNewCity)}
      {renderChipInput('countries', 'Preferred Countries', 'e.g., United States', newCountry, setNewCountry)}

      <IonItem>
        <IonLabel>
          <h3>Remote Work</h3>
          <p>Interested in fully remote positions</p>
        </IonLabel>
        <IonToggle
          checked={data.remote || false}
          onIonChange={(e) => handleToggle('remote', e.detail.checked)}
        />
      </IonItem>

      <IonItem>
        <IonLabel>
          <h3>Hybrid Work</h3>
          <p>Open to hybrid (remote + office) arrangements</p>
        </IonLabel>
        <IonToggle
          checked={data.hybrid_ok || false}
          onIonChange={(e) => handleToggle('hybrid_ok', e.detail.checked)}
        />
      </IonItem>

      <IonItem>
        <IonLabel>
          <h3>Willing to Relocate</h3>
          <p>Open to relocating for the right opportunity</p>
        </IonLabel>
        <IonToggle
          checked={data.relocation_willing || false}
          onIonChange={(e) => handleToggle('relocation_willing', e.detail.checked)}
        />
      </IonItem>
    </IonList>
  );
};

export default LocationPreferenceEditor;
